import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_DEFAULT = 3;

/**
 * ImageUploader — componente reutilizável (até 3 fotos).
 *
 * Props:
 *   - photos: array de { id, url, filename } (estado atual)
 *   - modulo: 'equipamento' | 'peca' | 'cliente' | 'cliente_crm' | 'chamado_problema' | 'chamado_solucao' | 'usuario'
 *   - refId: id do registro (parent)
 *   - onChange: callback(photos) ao adicionar/remover
 *   - max: número máximo de fotos (default 3)
 *   - label: texto explicativo (opcional)
 *   - readOnly: desabilita upload (default false)
 */
export const ImageUploader = ({
  photos = [],
  modulo,
  refId,
  onChange,
  max = MAX_DEFAULT,
  label,
  readOnly = false,
}) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const buildUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const token = localStorage.getItem("lab_token");
    // /api/files exige Bearer ou ?auth=token
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
  };

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Formato inválido. Use JPG, PNG ou WEBP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Imagem muito grande (${Math.round(file.size / 1024)} KB). Limite: 5 MB.`);
      return;
    }
    if (photos.length >= max) {
      toast.error(`Máximo de ${max} foto${max > 1 ? "s" : ""} atingido.`);
      return;
    }
    if (!refId) {
      toast.error("Salve o cadastro antes de adicionar fotos.");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("modulo", modulo);
      fd.append("ref_id", String(refId));
      const r = await api.post(`/upload/foto`, fd);
      const novaFoto = r.data;
      onChange?.([...photos, novaFoto]);
      toast.success("Foto adicionada!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  const remover = async (foto) => {
    if (!window.confirm("Remover esta foto?")) return;
    try {
      await api.delete(`/upload/foto/${foto.id}`);
      onChange?.(photos.filter((p) => p.id !== foto.id));
      toast.success("Foto removida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover");
    }
  };

  const slots = Math.max(0, max - photos.length);

  return (
    <div className="space-y-2" data-testid={`image-uploader-${modulo}`}>
      {label && (
        <div className="text-xs font-medium text-slate-600 uppercase tracking-wide">
          {label}{" "}
          <span className="text-slate-400 normal-case font-normal">
            ({photos.length}/{max}) · JPG, PNG ou WEBP · máx. 5 MB
          </span>
        </div>
      )}

      <div className={`grid gap-3 ${max === 1 ? "grid-cols-1 max-w-[200px]" : "grid-cols-2 sm:grid-cols-3"}`}>
        {photos.map((foto) => (
          <div
            key={foto.id}
            className="relative group border border-slate-200 rounded-lg overflow-hidden bg-slate-50 aspect-square"
            data-testid={`foto-${foto.id}`}
          >
            <img
              src={buildUrl(foto.url)}
              alt={foto.filename}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {!readOnly && (
              <button
                type="button"
                onClick={() => remover(foto)}
                className="absolute top-1.5 right-1.5 bg-red-500/90 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remover-foto-${foto.id}`}
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}

        {!readOnly && slots > 0 && (
          <button
            type="button"
            onClick={onPick}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`adicionar-foto-${modulo}`}
          >
            {uploading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span className="text-xs">Enviando...</span>
              </>
            ) : (
              <>
                <Upload size={18} />
                <span className="text-xs font-medium">Adicionar</span>
                <span className="text-[10px]">(opcional)</span>
              </>
            )}
          </button>
        )}
      </div>

      {photos.length === 0 && readOnly && (
        <div className="text-xs text-slate-400 flex items-center gap-1.5 py-2">
          <ImageIcon size={14} /> Sem fotos cadastradas
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
      />
    </div>
  );
};

/** Versão thumbnail para listagens — 1ª foto ou placeholder. */
export const PhotoThumb = ({ photos = [], size = 40, alt = "" }) => {
  const first = photos?.[0];
  const token = typeof window !== "undefined" ? localStorage.getItem("lab_token") : "";
  const src = first?.url
    ? (first.url.startsWith("http") ? first.url : `${process.env.REACT_APP_BACKEND_URL}${first.url}?auth=${token}`)
    : null;
  if (!src) {
    return (
      <div
        className="rounded bg-slate-100 flex items-center justify-center text-slate-300"
        style={{ width: size, height: size }}
      >
        <ImageIcon size={size * 0.45} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="rounded object-cover border border-slate-200"
      style={{ width: size, height: size }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
};

export default ImageUploader;
